import { apiCall, API_CONFIG } from './api';
import { authAPI } from './auth-api';
import { dispatchAPI } from './dispatch-api';

export const ordersAPI = {
  getOrdersList: async ({ page = 1, limit = 100, status, search, stats = false, filterType, date, month, year, startDate, endDate, courierPartner, timeSlot, driverId, adminId, productId, productNumericId, orderType } = {}) => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (status) queryParams.set('status', status);
      if (search) queryParams.set('search', search);
      if (stats) queryParams.set('stats', 'true');
      if (filterType) queryParams.set('filterType', filterType);
      if (date) queryParams.set('date', date);
      if (month) queryParams.set('month', month);
      if (year) queryParams.set('year', year);
      if (startDate) queryParams.set('startDate', startDate);
      if (endDate) queryParams.set('endDate', endDate);
      if (courierPartner) queryParams.set('courierPartner', courierPartner);
      if (timeSlot) queryParams.set('timeSlot', timeSlot);
      if (driverId) queryParams.set('driverId', driverId);
      if (adminId) queryParams.set('adminId', adminId);
      if (productId) queryParams.set('productId', productId);
      if (productNumericId != null && productNumericId !== '') {
        queryParams.set('productNumericId', String(productNumericId));
      }
      if (orderType && orderType !== 'all') queryParams.set('orderType', orderType);

      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.ORDERS}?${queryParams.toString()}`,
        { method: 'GET' }
      );

      if (response.success) {
        return {
          success: true,
          data: response.data?.orders || [],
          pagination: response.data?.pagination,
          stats: response.data?.stats,
          timeSlots: response.data?.timeSlots || [],
          msg: response.msg,
        };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to fetch orders',
      };
    } catch (error) {
      console.error('List orders error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  getOrdersMap: async ({
    status,
    search,
    filterType,
    date,
    month,
    year,
    startDate,
    endDate,
    courierPartner,
    timeSlot,
    driverId,
    adminId,
    productId,
    productNumericId,
    orderType,
  } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (driverId) queryParams.set('driverId', driverId);
      if (courierPartner) queryParams.set('courierPartner', courierPartner);
      if (status) queryParams.set('status', status);
      if (search) queryParams.set('search', search);
      if (filterType) queryParams.set('filterType', filterType);
      if (date) queryParams.set('date', date);
      if (month) queryParams.set('month', month);
      if (year) queryParams.set('year', year);
      if (startDate) queryParams.set('startDate', startDate);
      if (endDate) queryParams.set('endDate', endDate);
      if (timeSlot) queryParams.set('timeSlot', timeSlot);
      if (adminId) queryParams.set('adminId', adminId);
      if (productId) queryParams.set('productId', productId);
      if (productNumericId != null && productNumericId !== '') {
        queryParams.set('productNumericId', String(productNumericId));
      }
      if (orderType && orderType !== 'all') queryParams.set('orderType', orderType);

      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.ORDERS}/map?${queryParams.toString()}`,
        { method: 'GET' }
      );

      if (response.success) {
        return {
          success: true,
          data: response.data?.orders || [],
          stats: response.data?.stats || null,
          msg: response.msg,
        };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to fetch map orders',
      };
    } catch (error) {
      console.error('Map orders error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  getAdminPolling: async () => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.ORDERS}/poll`, {
        method: 'GET',
      });
      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }
      return {
        success: false,
        error: response.message || response.msg || 'Polling failed',
      };
    } catch (error) {
      console.error('Admin polling error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  getOrderDetail: async (orderId) => {
    try {
      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.ORDERS}/${orderId}`,
        { method: 'GET' }
      );

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to fetch order',
      };
    } catch (error) {
      console.error('Get order error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  createOrder: async (payload) => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.ORDERS, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to create order',
      };
    } catch (error) {
      console.error('Create order error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  extractCoordinates: async (mapLink) => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.ORDERS}/extract-coords`, {
        method: 'POST',
        body: JSON.stringify({ mapLink }),
      });

      if (response.success && response.coordinates) {
        return {
          success: true,
          coordinates: response.coordinates,
          resolvedUrl: response.resolvedUrl,
        };
      }

      return {
        success: false,
        error: response.msg || response.message || 'Failed to extract coordinates',
      };
    } catch (error) {
      console.error('Extract coordinates error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  updateOrder: async (orderId, payload) => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.ORDERS}/${orderId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to update order',
      };
    } catch (error) {
      console.error('Update order error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  getConfirmedOrders: async (params) => {
    if (params && typeof params === 'object' && !Array.isArray(params)) {
      return dispatchAPI.getConfirmedOrders(params.date, params.timeSlot);
    }
    return dispatchAPI.getConfirmedOrders(params);
  },

  confirmOrder: async (orderId) => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.ORDERS}/${orderId}/confirm`, {
        method: 'PATCH',
      });

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to confirm order',
      };
    } catch (error) {
      console.error('Confirm order error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  fetchDispatchBoard: async () => {
    try {
      const [ordersRes, driversRes] = await Promise.all([
        ordersAPI.getOrdersList({ page: 1, limit: 100, stats: true }),
        apiCall(
          `${API_CONFIG.ENDPOINTS.DRIVERS}?${new URLSearchParams({ page: '1', limit: '100' })}`,
          { method: 'GET' }
        ),
      ]);

      if (!ordersRes.success) {
        return { success: false, error: ordersRes.error || 'Failed to load orders' };
      }

      const drivers = driversRes.success ? (driversRes.data?.drivers || []) : [];
      const activeDrivers = drivers.filter((d) => d.status !== false);

      const unassignedOrders = (ordersRes.data || []).filter((order) => {
        const status = (order.orderStatus || '').toLowerCase();
        if (status === 'cancelled' || status === 'delivered') return false;
        if (!order.driverId) return true;
        if (typeof order.driverId === 'string' && !order.driverId.trim()) return true;
        return false;
      });

      return {
        success: true,
        data: {
          unassignedOrders,
          availableDrivers: activeDrivers,
          unassignedCount: ordersRes.stats?.unassignedOrders ?? unassignedOrders.length,
          activeDriverCount: activeDrivers.length,
        },
      };
    } catch (error) {
      console.error('Dispatch board error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  assignOrderToDriver: async (orderId, driverId) => {
    return ordersAPI.updateOrder(orderId, { driverId });
  },

  updateOrderStatus: async (orderId, status, extras = {}) => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.ORDERS}/update-status`, {
        method: 'POST',
        body: JSON.stringify({ orderId, status, ...extras }),
      });

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to update order status',
      };
    } catch (error) {
      console.error('Update order status error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  createReturnOrder: async (orderId, extras = {}) => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.ORDERS}/create-return`, {
        method: 'POST',
        body: JSON.stringify({ orderId, ...extras }),
      });

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to create return order',
      };
    } catch (error) {
      console.error('Create return order error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  cancelRelatedOrder: async (orderId, nextStatus) => {
    try {
      const response = await apiCall(`${API_CONFIG.ENDPOINTS.ORDERS}/cancel-related`, {
        method: 'POST',
        body: JSON.stringify({ orderId, nextStatus }),
      });

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to cancel the related order',
      };
    } catch (error) {
      console.error('Cancel related order error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  trackDelhivery: async (waybill) => {
    try {
      const queryParams = new URLSearchParams({ waybill: String(waybill || '').trim() });
      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.ORDERS}/track/delhivery?${queryParams.toString()}`,
        { method: 'GET' }
      );

      if (response.success) {
        return { success: true, data: response.data, msg: response.msg };
      }

      return {
        success: false,
        error: response.message || response.msg || 'Failed to fetch Delhivery tracking',
      };
    } catch (error) {
      console.error('Track Delhivery error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  downloadInvoice: async (orderId) => {
    try {
      const token = authAPI.getToken();
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ORDERS}/${orderId}/invoice`;
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        return { success: false, error: 'Failed to download invoice' };
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `invoice-${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      return { success: true };
    } catch (error) {
      console.error('Download invoice error:', error);
      return { success: false, error: 'Failed to download invoice' };
    }
  },
};
